import { IconActionButton, IconActionLink } from "./AdminActions";
import { useEffect, useState } from "react";
import { Modal } from "./Modal";

type MaterialPreviewModalProps = {
  title: string;
  eyebrow?: string;
  fileName: string;
  mimeType?: string;
  url: string;
  textContent?: string;
  onClose: () => void;
  closeLabel?: string;
  downloadLabel?: string;
  showDownload?: boolean;
};

export function MaterialPreviewModal({
  title,
  eyebrow = "Material",
  fileName,
  mimeType = "",
  url,
  textContent = "",
  onClose,
  closeLabel = "Cerrar",
  downloadLabel = "Descargar material",
  showDownload = true,
}: MaterialPreviewModalProps) {
  const previewKind = !url && textContent ? "text" : materialPreviewKind(fileName, mimeType);
  const powerpoint = isPowerPoint(fileName, mimeType);
  const previewUrl = powerpoint ? appendPreviewParameter(url) : url;
  const [documentUrl, setDocumentUrl] = useState("");
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (previewKind !== "document") return;
    const controller = new AbortController();
    setDocumentUrl("");
    setPreviewError("");
    void fetch(previewUrl, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({})) as { error?: string };
          throw new Error(payload.error || "No se pudo generar la vista previa");
        }
        const bytes = new Uint8Array(await response.arrayBuffer());
        const signature = new TextDecoder().decode(bytes.subarray(0, 5));
        if (bytes.length < 5 || signature !== "%PDF-") throw new Error("La conversión no produjo un PDF válido");
        setDocumentUrl(`${previewUrl}#toolbar=1&navpanes=0&view=FitH`);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setPreviewError(error instanceof Error ? error.message : "No se pudo mostrar la presentación");
      });
    return () => {
      controller.abort();
    };
  }, [previewKind, previewUrl]);

  return <Modal title={title} eyebrow={eyebrow} wide className="material-preview-modal" onClose={onClose}>
    <div className="material-preview-meta">
      <strong>{fileName}</strong>
      <span>{mimeType || "Archivo adjunto"}</span>
    </div>
    <div className={`material-preview-frame ${previewKind || "unavailable"}`}>
      {previewKind === "image" && <img src={url} alt={fileName} />}
      {previewKind === "audio" && <audio src={url} controls />}
      {previewKind === "video" && <video src={url} controls />}
      {previewKind === "text" && <article className="material-preview-text">{textContent}</article>}
      {previewKind === "document" && documentUrl && <iframe src={documentUrl} title={fileName} />}
      {previewKind === "document" && !documentUrl && <div className="material-preview-unavailable">
        {!previewError && <span className="material-preview-spinner" aria-hidden="true" />}
        {previewError && <span aria-hidden="true">!</span>}
        <strong>{previewError || "Preparando vista previa…"}</strong>
        <p>{previewError ? "Verificá que LibreOffice esté instalado en el servidor para visualizar PPT y PPTX." : powerpoint ? "Convirtiendo el PowerPoint a PDF. La primera visualización puede demorar unos segundos." : "Cargando el documento…"}</p>
      </div>}
      {!previewKind && <div className="material-preview-unavailable">
        <span aria-hidden="true">▣</span>
        <strong>Vista previa no disponible en el navegador</strong>
        <p>Este formato debe descargarse y abrirse con la aplicación correspondiente.</p>
      </div>}
    </div>
    <div className="modal-footer">
      {showDownload && <IconActionLink icon="download" label={downloadLabel} href={url} download={fileName} />}
      <IconActionButton icon="close" label={closeLabel} tone="primary" onClick={onClose} />
    </div>
  </Modal>;
}

function materialPreviewKind(fileName: string, mimeType: string) {
  const normalizedMime = mimeType.toLowerCase();
  if (normalizedMime.startsWith("image/")) return "image";
  if (normalizedMime.startsWith("audio/")) return "audio";
  if (normalizedMime.startsWith("video/")) return "video";
  if (normalizedMime === "application/pdf" || /\.pdf$/i.test(fileName) || isPowerPoint(fileName, mimeType)) return "document";
  return "";
}

function isPowerPoint(fileName: string, mimeType: string) {
  const normalizedMime = mimeType.toLowerCase();
  return /\.pptx?$/i.test(fileName) || normalizedMime === "application/vnd.ms-powerpoint" || normalizedMime === "application/vnd.openxmlformats-officedocument.presentationml.presentation";
}

function appendPreviewParameter(url: string) {
  return `${url}${url.includes("?") ? "&" : "?"}preview=pdf`;
}
